import type { SystemRole } from '@clockwise/clients/system-roles';
import {
  ApiError,
  groupRegistry,
  normalizePermissions,
  PERMISSION_REGISTRY,
  scopeLabel,
} from '@clockwise/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Pencil } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { AuditEntryList } from '@/components/custom/audit/audit-entry-list';
import { BackLink } from '@/components/custom/back-link';
import { CatalogFormSheet } from '@/components/custom/catalog-form-sheet';
import {
  DetailList,
  type DetailListRow,
  DetailListSkeleton,
} from '@/components/custom/detail-list';
import {
  PillTabs,
  PillTabsContent,
  PillTabsList,
  PillTabsTrigger,
} from '@/components/custom/pill-tabs';
import { RequirePermission } from '@/components/custom/require-permission';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermissionGuard } from '@/hooks/use-permission-guard';
import { auditApi, systemRolesApi } from '@/lib/api';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';

const TAB_VALUES = ['details', 'audit'] as const;
type TabValue = (typeof TAB_VALUES)[number];

const detailSearch = z.object({
  tab: z.enum(TAB_VALUES).optional(),
});

export const Route = createFileRoute('/_authenticated/setup/system-roles/$id/')({
  validateSearch: detailSearch,
  component: SystemRoleDetailPage,
});

const AUDIT_PAGE_SIZE = 50;
const DEFAULT_TAB: TabValue = 'details';

// Single source for the detail rows: drives both the loaded table and the
// skeleton's row count, so the two can't drift (and the layout won't jump).
const DETAIL_FIELDS: { label: string; value: (r: SystemRole) => ReactNode }[] = [
  { label: 'Name', value: (r) => r.name },
  {
    label: 'Description',
    value: (r) => r.description ?? <span className="text-muted-foreground">—</span>,
  },
  { label: 'Status', value: (r) => <StatusBadge active={r.active} /> },
  { label: 'Created', value: (r) => new Date(r.createdAt).toLocaleString() },
  { label: 'Updated', value: (r) => new Date(r.updatedAt).toLocaleString() },
];

function SystemRoleDetailPage() {
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const { allowed: canView, fallback } = usePermissionGuard('system_role.view', 'system roles');
  // The Audit tab reads /api/audit, which is gated by `audit.view`. Without it
  // the tab is hidden entirely rather than rendering a "Forbidden." panel.
  const canViewAudit = useHasPermission('audit.view');
  const requestedTab: TabValue = tab ?? DEFAULT_TAB;
  const activeTab: TabValue =
    requestedTab === 'audit' && !canViewAudit ? DEFAULT_TAB : requestedTab;

  const detail = useQuery({
    queryKey: queryKeys.systemRoles.detail(id),
    queryFn: () => systemRolesApi.get(id),
    enabled: canView,
  });

  const audit = useQuery({
    queryKey: queryKeys.audit.forEntity({
      entityType: 'system_roles',
      entityId: id,
      limit: AUDIT_PAGE_SIZE,
      offset: 0,
    }),
    queryFn: () =>
      auditApi.list({
        entityType: 'system_roles',
        entityId: id,
        limit: AUDIT_PAGE_SIZE,
        offset: 0,
      }),
    enabled: canView && canViewAudit,
  });

  const role = detail.data?.data ?? null;
  const [editOpen, setEditOpen] = useState(false);

  // Status changes are audited, so refresh both the list/detail caches and the
  // Audit tab shown on this page.
  const invalidateAfterStatusChange = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.systemRoles.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
  };

  const deactivateMutation = useMutation({
    mutationFn: () => systemRolesApi.remove(id),
    onSuccess: () => {
      toast.success('Deactivated');
      invalidateAfterStatusChange();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Deactivate failed'),
  });

  const restoreMutation = useMutation({
    mutationFn: () => systemRolesApi.restore(id),
    onSuccess: () => {
      toast.success('Reactivated');
      invalidateAfterStatusChange();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Restore failed'),
  });

  const statusBusy = deactivateMutation.isPending || restoreMutation.isPending;

  if (fallback) return fallback;

  return (
    <PageShell className="gap-6 overflow-y-auto">
      <div>
        <BackLink to="/setup/system-roles">Back to system roles</BackLink>
      </div>

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          {detail.isLoading ? (
            <>
              <Skeleton className="h-7 w-56" />
              <Skeleton className="h-4 w-40" />
            </>
          ) : detail.isError ? (
            <h1 className="text-2xl font-semibold text-destructive">
              {detail.error instanceof ApiError ? detail.error.message : 'Failed to load.'}
            </h1>
          ) : role ? (
            <>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold">{role.name}</h1>
                <StatusBadge active={role.active} />
              </div>
              {role.description ? (
                <p className="text-sm text-muted-foreground">{role.description}</p>
              ) : null}
            </>
          ) : (
            <h1 className="text-2xl font-semibold">Not found</h1>
          )}
        </div>
        <div className="flex items-center gap-2">
          <RequirePermission permission="system_role.edit">
            {role?.active ? (
              <Button
                variant="outline"
                disabled={statusBusy}
                onClick={() => {
                  if (confirm(`Deactivate "${role.name}"?`)) {
                    deactivateMutation.mutate();
                  }
                }}
              >
                Deactivate
              </Button>
            ) : role && !role.active ? (
              <Button
                variant="outline"
                disabled={statusBusy}
                onClick={() => restoreMutation.mutate()}
              >
                Reactivate
              </Button>
            ) : null}
            <Button disabled={!role} onClick={() => setEditOpen(true)}>
              <Pencil />
              Edit
            </Button>
          </RequirePermission>
        </div>
      </header>

      <PillTabs
        value={activeTab}
        onValueChange={(v) =>
          navigate({
            search: (s) => ({ ...s, tab: v === DEFAULT_TAB ? undefined : (v as TabValue) }),
            replace: true,
          })
        }
        className="rounded-xl border bg-card p-4"
      >
        <PillTabsList fullWidth>
          <PillTabsTrigger value="details">Details</PillTabsTrigger>
          {canViewAudit ? (
            <PillTabsTrigger value="audit">
              Audit
              {audit.data ? (
                <span className="ml-1 inline-flex items-center rounded-full border border-border bg-background px-2.5 py-0.5 text-xs font-medium text-foreground transition-colors focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none">
                  {audit.data.meta.total}
                </span>
              ) : null}
            </PillTabsTrigger>
          ) : null}
        </PillTabsList>

        <PillTabsContent value="details" className="space-y-3">
          <div className="surface-card rounded-md border">
            {detail.isLoading ? (
              <DetailListSkeleton rows={DETAIL_FIELDS.length} />
            ) : role ? (
              <DetailList rows={detailRows(role)} />
            ) : (
              <p className="p-4 text-sm text-muted-foreground">System role not found.</p>
            )}
          </div>

          {role ? <PermissionsSummary role={role} /> : null}
        </PillTabsContent>

        <PillTabsContent value="audit" className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Every state-changing operation, newest first.
          </p>
          <div className="rounded-md border">
            {audit.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : audit.isError ? (
              <p className="p-4 text-sm text-destructive">
                {audit.error instanceof ApiError
                  ? audit.error.message
                  : 'Failed to load audit log.'}
              </p>
            ) : (
              <AuditEntryList items={audit.data?.data ?? []} total={audit.data?.meta.total ?? 0} />
            )}
          </div>
        </PillTabsContent>
      </PillTabs>

      <CatalogFormSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        entityLabel="system role"
        entity={role}
        permissionsEditable
        submit={(body) => {
          if (!role) throw new Error('System role not loaded');
          return systemRolesApi.patch(role.id, {
            name: body.name,
            description: body.description,
            permissions: body.permissions ?? [],
          });
        }}
        invalidateKeys={[
          queryKeys.systemRoles.all,
          queryKeys.audit.all,
          queryKeys.systemRoles.detail(id),
        ]}
      />
    </PageShell>
  );
}

function detailRows(role: SystemRole): DetailListRow[] {
  return DETAIL_FIELDS.map((f) => ({ label: f.label, value: f.value(role) }));
}

/**
 * Read-only view of the role's granted permissions, grouped the same way the
 * editor lays them out. Only granted permissions are shown — scoped ones carry
 * their scope label. Normalized on read so a stale stored scope renders the
 * value the editor would coerce it to.
 */
function PermissionsSummary({ role }: { role: SystemRole }) {
  const granted = new Map(
    normalizePermissions(role.permissions).map((e) => [e.permission, e.scope]),
  );
  const groups = groupRegistry(PERMISSION_REGISTRY)
    .map(([group, defs]) => [group, defs.filter((d) => granted.has(d.id))] as const)
    .filter(([, defs]) => defs.length > 0);

  return (
    <section className="space-y-2">
      <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Permissions ({granted.size})
      </h2>
      <div className="rounded-md border p-3">
        {groups.length === 0 ? (
          <p className="text-sm text-muted-foreground">No permissions granted.</p>
        ) : (
          <div className="space-y-4">
            {groups.map(([group, defs]) => (
              <div key={group} className="space-y-1.5">
                <h3 className="text-xs font-semibold text-foreground">{group}</h3>
                <ul className="divide-y rounded-md border">
                  {defs.map((def) => {
                    const scope = granted.get(def.id);
                    return (
                      <li
                        key={def.id}
                        className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm"
                      >
                        <span>{def.label}</span>
                        {scope ? (
                          <span className="text-xs text-muted-foreground">
                            {scopeLabel(group, scope)}
                          </span>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function StatusBadge({ active }: { active: boolean }) {
  if (active) {
    return (
      <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      Inactive
    </span>
  );
}
